// httpPorts — the per-tab Context facet of Conversation: the three context
// write ports (introduce/drop/clear), the explicit-context read, and the
// ambient "nearby" bloom read. All keyed by the tab's anchor `EntityRef`.

import type * as Core from "@bilda/core";
import { Result } from "@bilda/utils";
import { mkMalformed } from "../externalError.js";
import type { ContextPorts } from "../ports/context.js";
import type { PortResult } from "../ports/result.js";
import type { AmbientItemDTO } from "../ports.js";
import { fields, list, num, str } from "../wireShape.js";
import { anchorWire, command } from "./shared.js";

const contextCommand = async (
    name: "introduceToContext" | "dropFromContext" | "clearContext",
    anchor: Core.EntityRef.Ref,
    ref: Core.Resource.Ref.Ref | undefined,
): PortResult<void> => {
    const wire = anchorWire(anchor);
    if (wire === null) {
        return Result.err(
            mkMalformed(`unsupported context anchor: ${anchor.type}`),
        );
    }
    const r = await command(name, {
        anchorKind: wire.kind,
        anchorId: wire.id,
        ...(ref !== undefined ? { resourceId: ref.id } : {}),
    });
    return Result.isErr(r) ? r : Result.ok(undefined);
};

const introduceToContext = (
    anchor: Core.EntityRef.Ref,
    ref: Core.Resource.Ref.Ref,
): PortResult<void> => contextCommand("introduceToContext", anchor, ref);

const dropFromContext = (
    anchor: Core.EntityRef.Ref,
    ref: Core.Resource.Ref.Ref,
): PortResult<void> => contextCommand("dropFromContext", anchor, ref);

const clearContext = (anchor: Core.EntityRef.Ref): PortResult<void> =>
    contextCommand("clearContext", anchor, undefined);

const loadContext = async (
    anchor: Core.EntityRef.Ref,
): PortResult<readonly string[]> => {
    const wire = anchorWire(anchor);
    if (wire === null) {
        return Result.err(
            mkMalformed(`unsupported context anchor: ${anchor.type}`),
        );
    }
    const r = await command("showContext", {
        anchorKind: wire.kind,
        anchorId: wire.id,
    });
    if (Result.isErr(r)) return r;
    const resourceIds = list(fields(r.value)?.get("resourceIds")).flatMap(
        (raw): readonly string[] => {
            const id = str(raw);
            return id === undefined ? [] : [id];
        },
    );
    return Result.ok(resourceIds);
};

// `showAmbientContext`'s wire shape is the workflow's `AmbientItem[]`
// verbatim (core's branded fields are plain numbers/strings at runtime, so
// no server-side DTO conversion was needed — see that route's contract
// entry). This parses it into the wire-safe `AmbientItemDTO` ports.ts
// declares, scoped to resource-kind neighbours only (see
// `loadAmbientContext`'s doc comment in ports.ts for why). A malformed or
// non-resource item is
// dropped, never surfaced as a parse error — one bad neighbour shouldn't
// blank the whole bloom.
const loadAmbientContext = async (
    anchor: Core.EntityRef.Ref,
): PortResult<readonly AmbientItemDTO[]> => {
    const wire = anchorWire(anchor);
    if (wire === null) {
        return Result.err(
            mkMalformed(`unsupported context anchor: ${anchor.type}`),
        );
    }
    const r = await command("showAmbientContext", {
        anchorKind: wire.kind,
        anchorId: wire.id,
    });
    if (Result.isErr(r)) return r;
    const items = list(fields(r.value)?.get("items")).flatMap(
        (raw): readonly AmbientItemDTO[] => {
            const f = fields(raw);
            const refFields = fields(f?.get("ref"));
            const refType = str(refFields?.get("type"));
            const resourceId = str(refFields?.get("id"));
            const score = num(f?.get("score"));
            const relation = str(fields(f?.get("via"))?.get("relation"));
            if (
                (refType !== "whole_resource_ref" &&
                    refType !== "part_resource_ref") ||
                resourceId === undefined ||
                score === undefined ||
                relation === undefined
            ) {
                return [];
            }
            return [{ resourceId, score, relation }];
        },
    );
    return Result.ok(items);
};

export const contextHttp: ContextPorts = {
    introduceToContext,
    dropFromContext,
    clearContext,
    loadContext,
    loadAmbientContext,
};
